/**
 * Neon Lightning Field Visualizer - Audio-reactive neon lightning bolts with branching
 *
 * Renders jagged lightning bolts that spawn on beats, with branching forks,
 * spark particles at joints, and strobe flashes. Driven by beat detection,
 * bass energy, and spectral flux. When audio features are unavailable
 * (features === null), a fallback beat detector approximates beats from
 * bass band amplitudes.
 *
 * Uses pre-allocated object pools (bolts + sparks) for zero-GC animation.
 * Overrides draw() with an offscreen canvas trail technique that properly respects
 * the global background controller (solid, gradient, pattern, or image backgrounds).
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'neon-lightning-field',
  displayName: 'Neon Lightning Field',
  contextType: '2d',
};

// Bolt states
const BOLT_DEAD = 0;
const BOLT_ALIVE = 1;

// Spark states
const SPARK_DEAD = 0;
const SPARK_ALIVE = 1;

// Pool sizes
const MAX_BOLTS = 24;
const MAX_SPARKS = 300;
const MAX_SEGMENTS = 20;       // Max segments per bolt polyline
const MAX_BRANCHES_PER_BOLT = 4;
const MAX_BRANCH_SEGMENTS = 6;

// Timing
const SPAWN_COOLDOWN_MS = 50;
const JITTER_HOLD_FRAMES = 3;

/**
 * Fast integer hash → 0-1 float. No allocation.
 * Uses multiply-xorshift to produce deterministic jitter.
 */
function fastHash(seed, a, b) {
  let h = (seed * 2654435761 + a * 340573321 + b * 1013904223) | 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x45d9f3b);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Parse a hex color string (#rrggbb) to an [r, g, b] array.
 */
function hexToRGB(hex) {
  if (!hex || hex.length < 7) return [0, 0, 0];
  return [
    parseInt(hex.slice(1, 3), 16) || 0,
    parseInt(hex.slice(3, 5), 16) || 0,
    parseInt(hex.slice(5, 7), 16) || 0,
  ];
}

class NeonLightningField extends Base2DVisualizer {
  static name = 'neon-lightning-field';
  static displayName = 'Neon Lightning Field';

  static getConfigUI() {
    return [
      { type: 'range', id: 'boltCount', label: 'Bolt Count', min: 1, max: 8, default: 3 },
      { type: 'range', id: 'boltSegments', label: 'Bolt Detail', min: 4, max: 18, default: 10 },
      { type: 'range', id: 'branchiness', label: 'Branchiness', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'strobeIntensity', label: 'Strobe Intensity', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'strobeDecay', label: 'Strobe Decay', min: 5, max: 50, default: 15, scale: 0.01 },
      { type: 'range', id: 'sparkDensity', label: 'Spark Density', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'sparkLife', label: 'Spark Life', min: 10, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 5, max: 50, default: 20 },
      { type: 'range', id: 'motionSpeed', label: 'Background Motion', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'trailFade', label: 'Trail Fade', min: 3, max: 30, default: 10, scale: 0.01 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 30, max: 100, default: 80, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 60, scale: 0.01 },
      { type: 'checkbox', id: 'glow', label: 'Glow Effect', default: true },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#00ccff' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#ff00ff' },
      { type: 'color', id: 'accentColor', label: 'Bolt Core Color', default: '#ffffff' },
    ];
  }

  init() {
    // --- Bolt pool ---
    this.bolts = new Array(MAX_BOLTS);
    this.boltFreeList = new Array(MAX_BOLTS);
    this.boltFreeCount = MAX_BOLTS;

    for (let i = 0; i < MAX_BOLTS; i++) {
      const branches = new Array(MAX_BRANCHES_PER_BOLT);
      for (let b = 0; b < MAX_BRANCHES_PER_BOLT; b++) {
        branches[b] = {
          segments: new Float32Array(MAX_BRANCH_SEGMENTS * 2),
          segCount: 0,
        };
      }
      this.bolts[i] = {
        state: BOLT_DEAD,
        segments: new Float32Array(MAX_SEGMENTS * 2),
        segmentCount: 0,
        branches,
        branchCount: 0,
        thickness: 2,
        alpha: 1,
        r: 0, g: 204, b: 255,
        glowR: 0, glowG: 100, glowB: 200,
        life: 0,
        maxLife: 12,
        jitterSeed: 0,
        jitterPhase: 0,
        freqIndex: 0,
        _startX: 0,
        _startY: 0,
        _endX: 0,
        _endY: 0,
        _jitterAmount: 0,
      };
      this.boltFreeList[i] = i;
    }

    // --- Spark pool ---
    this.sparks = new Array(MAX_SPARKS);
    this.sparkFreeList = new Array(MAX_SPARKS);
    this.sparkFreeCount = MAX_SPARKS;

    for (let i = 0; i < MAX_SPARKS; i++) {
      this.sparks[i] = {
        state: SPARK_DEAD,
        x: 0, y: 0,
        vx: 0, vy: 0,
        life: 0, maxLife: 0,
        alpha: 1, size: 2,
        r: 255, g: 255, b: 255,
        gravity: 0.15,
        friction: 0.97,
      };
      this.sparkFreeList[i] = i;
    }

    // --- Strobe state ---
    this.strobeAlpha = 0;

    // --- Beat fallback state ---
    this._prevBassAvg = 0;
    this._fallbackBeatCooldown = 0;

    // --- Spawn state ---
    this.lastSpawnTime = 0;
    this.ambientEnergy = 0;
    this.frameCount = 0;
    this.isFirstFrame = true;

    // --- Background noise phase ---
    this.bgNoisePhase = 0;

    // --- Trail offscreen canvas — stores previous frame for fade compositing ---
    this._trailCanvas = null;
    this._trailCtx = null;

    // --- Cached styles ---
    this._colorStartRGB = [0, 204, 255];
    this._colorEndRGB = [255, 0, 255];
    this._accentRGB = [255, 255, 255];
    this._lerpBuf = [0, 0, 0]; // Reusable buffer for _lerpColorRGB
    this._updateCachedStyles();
  }

  onConfigChange(changed) {
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined ||
        changed.accentColor !== undefined) {
      this._updateCachedStyles();
    }
  }

  getElementCount() {
    return 32;
  }

  // --- Pool allocation helpers ---

  _allocBolt() {
    if (this.boltFreeCount === 0) return -1;
    return this.boltFreeList[--this.boltFreeCount];
  }

  _freeBolt(index) {
    if (this.bolts[index].state === BOLT_DEAD) return;
    this.bolts[index].state = BOLT_DEAD;
    this.boltFreeList[this.boltFreeCount++] = index;
  }

  _allocSpark() {
    if (this.sparkFreeCount === 0) return -1;
    return this.sparkFreeList[--this.sparkFreeCount];
  }

  _freeSpark(index) {
    if (this.sparks[index].state === SPARK_DEAD) return;
    this.sparks[index].state = SPARK_DEAD;
    this.sparkFreeList[this.sparkFreeCount++] = index;
  }

  // --- Trail offscreen canvas ---

  /**
   * Ensure the trail offscreen canvas matches the main canvas dimensions.
   */
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  // --- Cached style helpers ---

  _updateCachedStyles() {
    this._colorStartRGB = hexToRGB(this.config.colorStart || '#00ccff');
    this._colorEndRGB = hexToRGB(this.config.colorEnd || '#ff00ff');
    this._accentRGB = hexToRGB(this.config.accentColor || '#ffffff');
  }

  /**
   * Interpolate between colorStart and colorEnd RGB tuples.
   * @param {number} t - 0 to 1
   * @returns {number[]} [r, g, b]
   */
  _lerpColorRGB(t) {
    const s = this._colorStartRGB;
    const e = this._colorEndRGB;
    const buf = this._lerpBuf;
    buf[0] = (s[0] + (e[0] - s[0]) * t) | 0;
    buf[1] = (s[1] + (e[1] - s[1]) * t) | 0;
    buf[2] = (s[2] + (e[2] - s[2]) * t) | 0;
    return buf;
  }

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Instead of overlaying a semi-transparent solid color (which ignores the global
   * background and always converges to that color), we:
   * 1. Save the current canvas (previous frame) to an offscreen buffer
   * 2. Draw the proper background via clearCanvas() → drawBackground()
   * 3. Composite the saved frame on top at reduced opacity (trail effect)
   * 4. Render new content
   *
   * This works correctly with solid colors, gradients, patterns, and images.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      // Trail effect via offscreen canvas compositing
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      //    trailFade controls fade speed: higher = faster fade (less persistence)
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailFade;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  // --- Main render ---

  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const now = performance.now();
    this.frameCount++;

    // --- Detect beat (with fallback) ---
    const isBeat = this._detectBeat(amplitudes, features);

    // --- Spawn bolts ---
    this._handleBoltSpawning(isBeat, amplitudes, features, now);

    // --- Strobe flash on beat ---
    if (isBeat && config.strobeIntensity > 0) {
      this.strobeAlpha = config.strobeIntensity;
    }

    // --- Background subtle noise shapes ---
    this._drawBackground(ctx, canvas);

    // --- Update and render bolts ---
    // Use source-over (default) — shadowBlur provides the neon glow effect.
    // Do NOT use 'lighter' here: additive blending accumulates through the
    // trail overlay and causes white blowout within a few frames.
    ctx.save();

    for (let i = 0; i < MAX_BOLTS; i++) {
      const bolt = this.bolts[i];
      if (bolt.state === BOLT_DEAD) continue;

      bolt.life--;

      // Re-jitter bolt shape periodically (skip on death frame to avoid wasted work)
      if (bolt.life > 0 && bolt.life % JITTER_HOLD_FRAMES === 0) {
        bolt.jitterPhase++;
        this._generateBoltGeometry(bolt);
      }

      // Fade out toward end of life
      const lifeRatio = bolt.life / bolt.maxLife;
      bolt.alpha = Math.min(1, lifeRatio * 2); // Full brightness first half, linear fade second half

      if (bolt.life <= 0) {
        this._freeBolt(i);
        continue;
      }

      // Draw bolt
      this._drawBolt(ctx, bolt);

      // Emit sparks at segment joints
      if (config.sparkDensity > 0) {
        this._emitSparksFromBolt(bolt, amplitudes);
      }
    }

    // --- Update and render sparks ---
    const dt = 1 / 60; // Assume ~60fps for physics
    for (let i = 0; i < MAX_SPARKS; i++) {
      const spark = this.sparks[i];
      if (spark.state === SPARK_DEAD) continue;

      spark.life -= dt;
      if (spark.life <= 0) {
        this._freeSpark(i);
        continue;
      }

      // Physics
      spark.vy += spark.gravity;
      spark.vx *= spark.friction;
      spark.vy *= spark.friction;
      spark.x += spark.vx;
      spark.y += spark.vy;

      // Quadratic alpha fadeout
      const lifeRatio = spark.life / spark.maxLife;
      spark.alpha = lifeRatio * lifeRatio;

      // Kill if off-screen
      if (spark.x < -20 || spark.x > canvas.width + 20 ||
          spark.y < -20 || spark.y > canvas.height + 20) {
        this._freeSpark(i);
        continue;
      }

      // Render spark
      const brightness = config.brightness;
      const r = Math.min(255, (spark.r * brightness) | 0);
      const g = Math.min(255, (spark.g * brightness) | 0);
      const b = Math.min(255, (spark.b * brightness) | 0);

      ctx.globalAlpha = spark.alpha;
      ctx.fillStyle = `rgb(${r},${g},${b})`;

      if (config.glow && spark.size > 1) {
        ctx.shadowBlur = spark.size * 3;
        ctx.shadowColor = `rgb(${r},${g},${b})`;
      }

      ctx.beginPath();
      ctx.arc(spark.x, spark.y, Math.max(0.3, spark.size * spark.alpha), 0, Math.PI * 2);
      ctx.fill();

      if (config.glow && spark.size > 1) {
        ctx.shadowBlur = 0;
      }
    }

    ctx.globalAlpha = 1;
    ctx.restore();

    // --- Strobe overlay ---
    // source-over: alpha-blends toward white, creating a bright flash
    // that fades naturally through the trail overlay. Safe from accumulation.
    if (this.strobeAlpha > 0.01) {
      ctx.globalAlpha = this.strobeAlpha;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;

      // Exponential decay
      this.strobeAlpha *= (1 - config.strobeDecay);
    }
  }

  // --- Beat detection ---

  _detectBeat(amplitudes, features) {
    // Use audio features if available
    if (features?.rhythm) {
      return features.rhythm.isOnset;
    }

    // Fallback: detect beats from bass amplitudes
    if (this._fallbackBeatCooldown > 0) {
      this._fallbackBeatCooldown--;
      return false;
    }

    const bassEnd = Math.max(1, Math.floor(amplitudes.length * 0.2));
    let bassSum = 0;
    for (let i = 0; i < bassEnd; i++) {
      bassSum += amplitudes[i];
    }
    const bassAvg = bassSum / bassEnd;

    const isBeat = bassAvg > this._prevBassAvg * 1.4 && bassAvg > 0.3;
    this._prevBassAvg = this._prevBassAvg * 0.85 + bassAvg * 0.15;

    if (isBeat) {
      this._fallbackBeatCooldown = 8;
    }

    return isBeat;
  }

  // --- Bolt spawning ---

  _handleBoltSpawning(isBeat, amplitudes, features, now) {
    const { config } = this;
    const timeSinceSpawn = now - this.lastSpawnTime;

    // Beat-triggered spawn
    if (isBeat && timeSinceSpawn > SPAWN_COOLDOWN_MS) {
      const count = config.boltCount;
      for (let i = 0; i < count; i++) {
        this._spawnBolt(amplitudes);
      }
      this.lastSpawnTime = now;
    }

    // Spectral flux spawn — extra bolts on high transients
    const flux = this.spectralFlux;
    if (flux > 0.5 && timeSinceSpawn > SPAWN_COOLDOWN_MS) {
      const extraCount = Math.min(2, Math.ceil((flux - 0.5) * 4));
      for (let i = 0; i < extraCount; i++) {
        this._spawnBolt(amplitudes);
      }
      this.lastSpawnTime = now;
    }

    // Ambient energy accumulator for quiet passages
    const overallEnergy = (this.bassEnergy + this.midEnergy + this.highEnergy) / 3;
    this.ambientEnergy += overallEnergy * 0.02 + 0.002; // Small constant so silence still spawns
    if (this.ambientEnergy > 0.6) {
      this._spawnBolt(amplitudes);
      this.ambientEnergy = 0;
      this.lastSpawnTime = now;
    }
  }

  // --- Bolt creation ---

  _spawnBolt(amplitudes) {
    const idx = this._allocBolt();
    if (idx === -1) return;

    const { canvas, config } = this;
    const bolt = this.bolts[idx];
    const w = canvas.width;
    const h = canvas.height;

    // Audio-reactive parameters
    const energy = (this.bassEnergy + this.midEnergy) / 2;

    bolt.state = BOLT_ALIVE;
    bolt.segmentCount = config.boltSegments;
    bolt.branchCount = 0;
    bolt.jitterSeed = (Math.random() * 100000) | 0;
    bolt.jitterPhase = 0;
    bolt.maxLife = 8 + Math.floor(Math.random() * 13); // 8-20 frames
    bolt.life = bolt.maxLife;
    bolt.alpha = 1;
    bolt.thickness = 1.5 + energy * 3;
    bolt.freqIndex = (Math.random() * this.getElementCount()) | 0;

    // Color from frequency mapping
    const t = bolt.freqIndex / this.getElementCount();
    const color = this._lerpColorRGB(t);
    bolt.r = color[0];
    bolt.g = color[1];
    bolt.b = color[2];

    // Glow color: dimmer version of main color
    bolt.glowR = (color[0] * 0.5) | 0;
    bolt.glowG = (color[1] * 0.5) | 0;
    bolt.glowB = (color[2] * 0.5) | 0;

    // Determine spawn type for "field" effect
    const roll = Math.random();
    let startX, startY, endX, endY;

    if (roll < 0.4) {
      // Top-down (classic lightning)
      startX = w * (0.1 + Math.random() * 0.8);
      startY = h * (0.0 + Math.random() * 0.15);
      endX = startX + (Math.random() - 0.5) * w * 0.4;
      endY = h * (0.5 + Math.random() * 0.45);
    } else if (roll < 0.7) {
      // Side-to-side (horizontal bolts)
      const fromLeft = Math.random() < 0.5;
      startX = fromLeft ? w * 0.02 : w * 0.98;
      startY = h * (0.1 + Math.random() * 0.8);
      endX = fromLeft ? w * (0.5 + Math.random() * 0.45) : w * (0.05 + Math.random() * 0.45);
      endY = startY + (Math.random() - 0.5) * h * 0.4;
    } else {
      // Center-radiating
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.min(w, h) * (0.3 + Math.random() * 0.3);
      startX = w * (0.3 + Math.random() * 0.4);
      startY = h * (0.3 + Math.random() * 0.4);
      endX = startX + Math.cos(angle) * radius;
      endY = startY + Math.sin(angle) * radius;
    }

    // Store start/end for geometry generation
    bolt._startX = startX;
    bolt._startY = startY;
    bolt._endX = endX;
    bolt._endY = endY;
    bolt._jitterAmount = 20 + energy * 40;

    // Generate initial geometry
    this._generateBoltGeometry(bolt);

    // Generate branches
    this._generateBranches(bolt, energy);
  }

  /**
   * Generate the polyline segments for a bolt using perpendicular jitter.
   * Deterministic from seed + phase so the bolt holds its shape for a few frames.
   */
  _generateBoltGeometry(bolt) {
    const segs = bolt.segmentCount;
    const startX = bolt._startX;
    const startY = bolt._startY;
    const endX = bolt._endX;
    const endY = bolt._endY;
    const jitter = bolt._jitterAmount;
    const seed = bolt.jitterSeed;
    const phase = bolt.jitterPhase;

    // Direction vector
    const dx = endX - startX;
    const dy = endY - startY;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    // Perpendicular unit vector
    const perpX = -dy / len;
    const perpY = dx / len;

    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      // Base position along the line
      let x = startX + dx * t;
      let y = startY + dy * t;

      // Perpendicular jitter, max in middle, zero at endpoints
      if (i > 0 && i < segs) {
        const envelope = Math.sin(t * Math.PI);
        const hash = fastHash(seed, i, phase) * 2 - 1; // -1 to 1
        const offset = hash * jitter * envelope;
        x += perpX * offset;
        y += perpY * offset;
      }

      bolt.segments[i * 2] = x;
      bolt.segments[i * 2 + 1] = y;
    }
  }

  /**
   * Generate branch forks from bolt segment joints.
   */
  _generateBranches(bolt, energy) {
    const { config } = this;
    const segs = bolt.segmentCount;
    const branchChance = config.branchiness * (0.3 + energy * 0.7);
    bolt.branchCount = 0;

    for (let i = 1; i < segs && bolt.branchCount < MAX_BRANCHES_PER_BOLT; i++) {
      if (Math.random() > branchChance) continue;

      const branch = bolt.branches[bolt.branchCount];
      const jointX = bolt.segments[i * 2];
      const jointY = bolt.segments[i * 2 + 1];

      // Direction from previous segment to this joint
      const pdx = jointX - bolt.segments[(i - 1) * 2];
      const pdy = jointY - bolt.segments[(i - 1) * 2 + 1];
      const plen = Math.sqrt(pdx * pdx + pdy * pdy) || 1;

      // Deviate 30-60 degrees from bolt direction
      const baseAngle = Math.atan2(pdy, pdx);
      const deviation = (30 + Math.random() * 30) * Math.PI / 180;
      const branchAngle = baseAngle + (Math.random() < 0.5 ? deviation : -deviation);

      const branchSegs = 3 + Math.floor(Math.random() * 3); // 3-5 segments
      const branchLen = plen * (0.4 + Math.random() * 0.6);
      const stepLen = branchLen / branchSegs;

      branch.segCount = branchSegs + 1;

      let bx = jointX;
      let by = jointY;

      for (let s = 0; s <= branchSegs; s++) {
        branch.segments[s * 2] = bx;
        branch.segments[s * 2 + 1] = by;

        if (s < branchSegs) {
          // Walk with slight random deviation
          const jitterAngle = branchAngle + (Math.random() - 0.5) * 0.6;
          bx += Math.cos(jitterAngle) * stepLen;
          by += Math.sin(jitterAngle) * stepLen;
        }
      }

      bolt.branchCount++;
    }
  }

  // --- Drawing ---

  _drawBolt(ctx, bolt) {
    const { config } = this;
    const brightness = config.brightness;

    // Scale colors by brightness
    const coreR = Math.min(255, (this._accentRGB[0] * brightness) | 0);
    const coreG = Math.min(255, (this._accentRGB[1] * brightness) | 0);
    const coreB = Math.min(255, (this._accentRGB[2] * brightness) | 0);
    const mainR = Math.min(255, (bolt.r * brightness) | 0);
    const mainG = Math.min(255, (bolt.g * brightness) | 0);
    const mainB = Math.min(255, (bolt.b * brightness) | 0);

    // --- Main bolt: outer glow pass ---
    // With source-over, shadowBlur creates the neon halo naturally.
    ctx.globalAlpha = bolt.alpha * 0.5;
    ctx.lineWidth = bolt.thickness * 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = `rgb(${mainR},${mainG},${mainB})`;

    if (config.glow) {
      ctx.shadowBlur = config.glowIntensity * 1.5;
      ctx.shadowColor = `rgb(${mainR},${mainG},${mainB})`;
    }

    this._strokePolyline(ctx, bolt.segments, bolt.segmentCount + 1);

    // --- Main bolt: core pass ---
    ctx.globalAlpha = bolt.alpha;
    ctx.lineWidth = bolt.thickness;
    ctx.strokeStyle = `rgb(${coreR},${coreG},${coreB})`;

    if (config.glow) {
      ctx.shadowBlur = config.glowIntensity;
      ctx.shadowColor = `rgb(${mainR},${mainG},${mainB})`;
    }

    this._strokePolyline(ctx, bolt.segments, bolt.segmentCount + 1);

    ctx.shadowBlur = 0;

    // --- Branches ---
    for (let b = 0; b < bolt.branchCount; b++) {
      const branch = bolt.branches[b];
      if (branch.segCount < 2) continue;

      // Branch glow pass
      ctx.globalAlpha = bolt.alpha * 0.35;
      ctx.lineWidth = bolt.thickness * 2;
      ctx.strokeStyle = `rgb(${mainR},${mainG},${mainB})`;

      if (config.glow) {
        ctx.shadowBlur = config.glowIntensity;
        ctx.shadowColor = `rgb(${mainR},${mainG},${mainB})`;
      }

      this._strokePolyline(ctx, branch.segments, branch.segCount);

      // Branch core pass
      ctx.globalAlpha = bolt.alpha * 0.8;
      ctx.lineWidth = Math.max(0.5, bolt.thickness * 0.6);
      ctx.strokeStyle = `rgb(${coreR},${coreG},${coreB})`;
      ctx.shadowBlur = 0;

      this._strokePolyline(ctx, branch.segments, branch.segCount);
    }
  }

  /**
   * Stroke a polyline from a Float32Array of [x0, y0, x1, y1, ...] pairs.
   */
  _strokePolyline(ctx, segments, pointCount) {
    if (pointCount < 2) return;
    ctx.beginPath();
    ctx.moveTo(segments[0], segments[1]);
    for (let i = 1; i < pointCount; i++) {
      ctx.lineTo(segments[i * 2], segments[i * 2 + 1]);
    }
    ctx.stroke();
  }

  // --- Spark emission ---

  _emitSparksFromBolt(bolt, amplitudes) {
    const { config } = this;
    const highE = this.highEnergy;

    // Only emit sparks when treble is prominent
    if (highE < 0.3) return;

    const sparkChance = config.sparkDensity * highE;

    // Walk bolt segments, possibly emitting a spark at each joint
    for (let i = 1; i < bolt.segmentCount; i++) {
      if (Math.random() > sparkChance * 0.15) continue;

      const idx = this._allocSpark();
      if (idx === -1) return;

      const spark = this.sparks[idx];
      spark.state = SPARK_ALIVE;
      spark.x = bolt.segments[i * 2];
      spark.y = bolt.segments[i * 2 + 1];

      // Small radial velocity with slight upward bias
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.5 + Math.random() * 2;
      spark.vx = Math.cos(angle) * speed;
      spark.vy = Math.sin(angle) * speed - 0.5; // Upward bias

      spark.maxLife = 0.3 + Math.random() * 0.5 * config.sparkLife;
      spark.life = spark.maxLife;
      spark.alpha = 1;
      spark.size = 1 + Math.random() * 2;
      spark.gravity = 0.1 + Math.random() * 0.1;
      spark.friction = 0.96 + Math.random() * 0.03;

      // Brighter than bolt color (+50 offset, clamped)
      spark.r = Math.min(255, bolt.r + 50);
      spark.g = Math.min(255, bolt.g + 50);
      spark.b = Math.min(255, bolt.b + 50);
    }
  }

  // --- Background ---

  _drawBackground(ctx, canvas) {
    const { config } = this;
    const speed = config.motionSpeed;
    this.bgNoisePhase += speed * 0.02;

    const w = canvas.width;
    const h = canvas.height;

    // Subtle large rectangles for ambient noise-like texture
    ctx.save();
    ctx.globalAlpha = 0.025;

    for (let i = 0; i < 8; i++) {
      // Deterministic pseudo-random positions from bgNoisePhase
      const px = (Math.sin(this.bgNoisePhase * 0.3 + i * 1.7) * 0.5 + 0.5) * w;
      const py = (Math.cos(this.bgNoisePhase * 0.2 + i * 2.3) * 0.5 + 0.5) * h;
      const size = 50 + (Math.sin(i * 3.1 + this.bgNoisePhase * 0.1) * 0.5 + 0.5) * 150;

      // Dim color from the palette
      const t = i / 8;
      const col = this._lerpColorRGB(t);
      ctx.fillStyle = `rgb(${(col[0] * 0.3) | 0},${(col[1] * 0.3) | 0},${(col[2] * 0.3) | 0})`;
      ctx.fillRect(px - size / 2, py - size / 2, size, size);
    }

    // A few thin strokes for subtle ambience
    ctx.globalAlpha = 0.03;
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const x1 = (Math.sin(this.bgNoisePhase * 0.15 + i * 4.1) * 0.5 + 0.5) * w;
      const y1 = (Math.cos(this.bgNoisePhase * 0.1 + i * 2.7) * 0.5 + 0.5) * h;
      const x2 = (Math.sin(this.bgNoisePhase * 0.12 + i * 3.3) * 0.5 + 0.5) * w;
      const y2 = (Math.cos(this.bgNoisePhase * 0.17 + i * 1.9) * 0.5 + 0.5) * h;

      const t = i / 4;
      const col = this._lerpColorRGB(t);
      ctx.strokeStyle = `rgb(${(col[0] * 0.4) | 0},${(col[1] * 0.4) | 0},${(col[2] * 0.4) | 0})`;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    ctx.restore();
  }

  // --- Clear / reset ---

  clear() {
    super.clear();
    this.isFirstFrame = true;

    // Return all bolts to pool
    this.boltFreeCount = MAX_BOLTS;
    for (let i = 0; i < MAX_BOLTS; i++) {
      this.bolts[i].state = BOLT_DEAD;
      this.boltFreeList[i] = i;
    }

    // Return all sparks to pool
    this.sparkFreeCount = MAX_SPARKS;
    for (let i = 0; i < MAX_SPARKS; i++) {
      this.sparks[i].state = SPARK_DEAD;
      this.sparkFreeList[i] = i;
    }

    this.strobeAlpha = 0;
    this.ambientEnergy = 0;
    this.lastSpawnTime = 0;
    this._prevBassAvg = 0;
    this._fallbackBeatCooldown = 0;
    this.frameCount = 0;
    this.bgNoisePhase = 0;
    // Clear trail buffer so stale frames don't flash on restart
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(NeonLightningField);

export { NeonLightningField };
export default NeonLightningField;
